import { consumptionHooks, propertyHooks } from '$src/lib/trpcHooks'
import { Button, Loader, Stack } from '@mantine/core'
import { AppPage } from '@markku/components'
import type { Consumption } from '@markku/types'
import { IconPlus } from '@tabler/icons-react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { ConsumptionChart } from './ConsumptionChart'
import { ConsumptionGuidance } from './ConsumptionGuidance'
import { ConsumptionSummaryCards } from './ConsumptionSummaryCards'
import { ConsumptionTable } from './ConsumptionTable'
import {
  calculateSummaryData,
  generateAvailableYears,
  generateMonthlyChartData,
  sortConsumptionsByDate,
} from './consumptionCalculations'
import { ConsumptionModal } from './ConsumptionModal'
import { enrichConsumptionsWithValues } from './utils'

export function ConsumptionPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/consumption' })
  const propertyId = (search as any).propertyId
    ? Number((search as any).propertyId)
    : undefined

  // Redirect to frontpage if no property selected
  useEffect(() => {
    if (!propertyId) {
      navigate({ to: '/' })
    }
  }, [propertyId, navigate])

  const [selectedType, setSelectedType] = useState<
    'electricity' | 'water' | 'heating'
  >('electricity')
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  )
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null)
  const [editingConsumption, setEditingConsumption] =
    useState<Consumption | null>(null)

  const { mutateAsync: deleteConsumption } = consumptionHooks.useDelete()

  // Fetch properties to get property info
  const { data: propertiesData, isLoading: propertiesLoading } =
    propertyHooks.useList()
  const properties = propertiesData?.data || []
  const currentProperty = properties.find((p) => p.id === propertyId)

  // Fetch consumption data: selectedYear - 2 to selectedYear
  const { data: consumptionsData, isLoading: consumptionsLoading } =
    consumptionHooks.useList({
      pageSize: 1000,
      filters: {
        $and: [
          { propertyId: { eq: propertyId } },
          { date: { gte: `${selectedYear - 2}-01-01` } },
          { date: { lte: `${selectedYear}-12-31` } },
        ],
      },
    })

  const allConsumptions = consumptionsData?.data || []

  // Enrich and filter consumptions
  const enrichedConsumptions = useMemo(
    () => enrichConsumptionsWithValues(allConsumptions),
    [allConsumptions],
  )

  const consumptions = useMemo(
    () =>
      enrichedConsumptions.filter((c) => {
        const year = new Date(c.date).getFullYear()
        return year === selectedYear
      }),
    [enrichedConsumptions, selectedYear],
  )

  const previousYearConsumptions = useMemo(
    () =>
      enrichedConsumptions.filter((c) => {
        const year = new Date(c.date).getFullYear()
        return year === selectedYear - 1
      }),
    [enrichedConsumptions, selectedYear],
  )

  // Calculate data using utility functions
  const summaryData = useMemo(
    () => calculateSummaryData(consumptions, previousYearConsumptions),
    [consumptions, previousYearConsumptions],
  )

  const monthlyData = useMemo(
    () =>
      generateMonthlyChartData(
        consumptions,
        previousYearConsumptions,
        selectedType,
      ),
    [consumptions, previousYearConsumptions, selectedType],
  )

  const availableYears = useMemo(() => generateAvailableYears(), [])

  const sortedConsumptions = useMemo(
    () => sortConsumptionsByDate(consumptions),
    [consumptions],
  )

  const handleEdit = (consumption: Consumption) => {
    setEditingConsumption(consumption)
    setModalMode('edit')
  }

  const handleDelete = (id: number) => {
    deleteConsumption({ id })
  }

  const handleAddConsumption = (type: 'electricity' | 'water' | 'heating') => {
    setSelectedType(type)
    setModalMode('add')
  }

  if (propertiesLoading || consumptionsLoading) {
    return (
      <AppPage
        title="Kulutusseuranta"
        subtitle={`Vuoden ${selectedYear} kulutus`}
        backTarget="/maintenance"
      >
        <Stack align="center" justify="center" h={300}>
          <Loader size="lg" />
        </Stack>
      </AppPage>
    )
  }

  const chartUnit =
    consumptions.find((c) => c.type === selectedType)?.unit || ''

  return (
    <AppPage
      title="Kulutusseuranta"
      subtitle={
        currentProperty
          ? `${currentProperty.address?.street}, ${currentProperty.address?.city}`
          : `Vuoden ${selectedYear} kulutus`
      }
      backTarget="/maintenance"
      rightSection={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setModalMode('add')}
        >
          Lisää
        </Button>
      }
    >
      <Stack gap="lg">
        {consumptions.length === 0 && <ConsumptionGuidance />}

        <ConsumptionSummaryCards
          summaryData={summaryData}
          onAddClick={handleAddConsumption}
        />

        <ConsumptionChart
          selectedType={selectedType}
          selectedYear={selectedYear}
          monthlyData={monthlyData}
          onTypeChange={setSelectedType}
          onYearChange={setSelectedYear}
          availableYears={availableYears}
          unit={chartUnit}
        />

        <ConsumptionTable
          consumptions={sortedConsumptions}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Stack>

      {modalMode && propertyId && (
        <ConsumptionModal
          mode={modalMode}
          opened={!!modalMode}
          onClose={() => {
            setModalMode(null)
            setEditingConsumption(null)
          }}
          propertyId={propertyId}
          allConsumptions={allConsumptions}
          consumption={modalMode === 'edit' ? editingConsumption! : undefined}
          defaultType={modalMode === 'add' ? selectedType : undefined}
        />
      )}
    </AppPage>
  )
}
